/**
 * A sketched loop read the way a person meant it — "what you drew, made perfect" — never by
 * guessing from the plan. A rough loop becomes:
 *
 *   drawn   – its own corners, made sharp where its sides meet (each corner is where the two
 *             fitted sides cross, so a corner the finger rounded off is a true corner again),
 *             straight sides straight, curved sides true arcs through round points;
 *   rect    – the rectangle it stands for, turned the way its sides run;
 *   circle  – the circle of the same size;
 *   ellipse – the ellipse of the same area and spread.
 *
 * One of them is the reading offered first (`primary`), the others are a tap away. Corners are
 * found where the outline turns sharply (ShortStraw's idea of a straw across a few samples, with
 * the turn measured instead of the straw), where the finger slowed down while turning (people
 * slow at corners they mean: Sezgin et al. 2001), and — overriding everything — where the finger
 * rested on purpose (`pinned`). The plan only comes in afterwards, when the editor lands each
 * corner exactly on the plan's corner beneath it.
 */
import type { Form, Vec } from '../../shared/doc.ts';
import { flattenPath, type CurvePath } from './curve.ts';
import { edgeCubics, type Cubic, type PolyForm } from './form.ts';

export type ReadingKey = 'drawn' | 'rect' | 'circle' | 'ellipse';
export const READINGS: readonly ReadingKey[] = ['drawn', 'rect', 'circle', 'ellipse'];

export interface SketchReading {
  primary: ReadingKey;
  forms: Record<ReadingKey, Form>;
  /** How many of the finger's rests became corners. */
  pinned: number;
}

export interface SketchOptions {
  /** A fingertip's slack at the current zoom, image px. */
  tol: number;
  /** When each stroke point was drawn (ms), for the finger's speed. */
  times?: readonly number[];
  /** Where the finger rested while drawing: corners the person placed. */
  pinned?: readonly Vec[];
}

const N = 128; // samples round a loop
const W = 4; // the turn at a sample is measured across this many samples either side

const v = (x: number, y: number): Vec => ({ x, y });
const sub = (a: Vec, b: Vec): Vec => v(a.x - b.x, a.y - b.y);
const dist = (a: Vec, b: Vec): number => Math.hypot(a.x - b.x, a.y - b.y);
const cross = (a: Vec, b: Vec): number => a.x * b.y - a.y * b.x;
const dot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;
const DEG = Math.PI / 180;

/* ----------------------------------------------------------------- reading */

/** The readings of a closed sketch (null when it is too small or too thin to be a shape). */
export function readSketch(stroke: readonly number[], opts: SketchOptions): SketchReading | null {
  const { tol } = opts;
  const raw: Vec[] = [];
  const rawT: number[] = [];
  for (let i = 0; i + 1 < stroke.length; i += 2) {
    const p = v(stroke[i]!, stroke[i + 1]!);
    if (raw.length && dist(p, raw[raw.length - 1]!) < 0.5) continue;
    raw.push(p);
    rawT.push(opts.times?.[i / 2] ?? NaN);
  }
  if (raw.length < 8) return null;
  // a few hundred points are plenty to find where the loop closes
  const coarse = resample(raw, rawT, Math.min(360, raw.length), false);
  const loop = closeLoop(coarse.P, coarse.T);
  if (loop.P.length < 8) return null;
  const { P: Q, T: QT } = resample(loop.P, loop.T, N, true);
  const per = perimeter(Q);
  const mom = moments(Q);
  if (Math.abs(mom.area) < Math.max(4 * tol * tol, 0.004 * per * per)) return null;
  const ell = ellipseOf(mom);

  // how round is it: every sample's distance from the fitted ellipse, as a share of its radius there
  let sumErr = 0,
    maxErr = 0;
  for (const q of Q) {
    const d = rotate(sub(q, v(ell.cx, ell.cy)), -ell.rot);
    const e = Math.abs(Math.hypot(d.x / ell.rx, d.y / ell.ry) - 1);
    sumErr += e;
    maxErr = Math.max(maxErr, e);
  }
  const round = sumErr / N <= 0.055 && maxErr <= 0.14;

  // corners
  const turn4 = Q.map((_, i) => turnAt(Q, i, W));
  const slow = slowness(QT);
  const reach = Math.max(1.5 * tol, 0.03 * per);
  const pinnedIdx: number[] = [];
  for (const p of opts.pinned ?? []) {
    let best = -1,
      bd = reach;
    Q.forEach((q, i) => {
      const d = dist(p, q);
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    if (best >= 0 && !pinnedIdx.includes(best)) pinnedIdx.push(best);
  }
  const corners = findCorners(turn4, slow, pinnedIdx);
  const pinnedCorners = pinnedIdx.filter((i) => corners.some((c) => cyclicGap(c, i, N) <= 2)).length;
  // a corner the finger rested on is exactly where it rested
  const exact = new Map<number, Vec>();
  for (const i of corners) {
    const k = pinnedIdx.findIndex((j) => cyclicGap(i, j, N) <= 2);
    if (k >= 0) exact.set(i, nearestPinned(opts.pinned!, Q[i]!));
  }

  const drawn = corners.length ? fitPieces(Q, corners, exact, tol) : roundLoop(Q, tol);
  const lines = drawn.lines;
  const rect = rectOf(Q, mom, lines);
  const circleR = Q.reduce((s, q) => s + dist(q, v(ell.cx, ell.cy)), 0) / N;
  const forms: Record<ReadingKey, Form> = {
    drawn: drawn.form,
    rect,
    circle: { f: 'ellipse', cx: ell.cx, cy: ell.cy, rx: circleR, ry: circleR, rot: 0 },
    ellipse: { f: 'ellipse', cx: ell.cx, cy: ell.cy, rx: ell.rx, ry: ell.ry, rot: ell.rot },
  };
  // which outline does the loop keep closer to, on average: the rectangle's or the ellipse's? (a
  // square drawn with its corners rounded off is still round-ish; it hugs its rectangle closer)
  const rp = formCorners(rect);
  let rRes = 0,
    eRes = 0;
  for (const q of Q) {
    rRes += distToLoop(q, rp);
    const d = rotate(sub(q, v(ell.cx, ell.cy)), -ell.rot);
    const rho = Math.hypot(d.x / ell.rx, d.y / ell.ry);
    eRes += rho > 1e-9 ? (Math.abs(rho - 1) * Math.hypot(d.x, d.y)) / rho : 0;
  }
  rRes /= N;
  eRes /= N;
  const size = Math.sqrt(Math.abs(mom.area));
  let primary: ReadingKey = 'drawn';
  if (pinnedCorners >= 2) primary = 'drawn';
  else if (round && eRes < 0.8 * rRes && !corners.some((i) => turn4[i]! >= 75 * DEG)) primary = Math.max(ell.rx, ell.ry) / Math.min(ell.rx, ell.ry) <= 1.18 ? 'circle' : 'ellipse';
  else if (rectLike(drawn.form, lines)) primary = 'rect';
  else {
    // a few clear points with real corners (a D, a wedge, a triangle) are what was meant, as drawn;
    // a loop that hugs its rectangle closer than anything else is that rectangle
    const nc = drawn.form.round.filter((r) => !r).length;
    const simple = nc >= 2 && nc !== 4 && drawn.form.round.length <= 6;
    if (!simple && rRes < 0.8 * eRes && rRes <= 0.045 * size) primary = 'rect';
  }
  return { primary, forms, pinned: pinnedCorners };
}

/**
 * An open stretch of outline drawn by the finger (the Pen's Draw) as the points that make it: its
 * two ends (corners), a corner wherever it turns sharply or the finger slowed to turn, and between
 * them a straight side where it ran straight, else the fewest round points whose curve follows it.
 * A corner between two straight sides is where their lines cross (a rounded-off corner is sharp).
 */
export function fitOpenStroke(stroke: readonly number[], opts: { tol: number; times?: readonly number[] }): { pts: Vec[]; round: number[] } | null {
  const { tol } = opts;
  const raw: Vec[] = [];
  const rawT: number[] = [];
  for (let i = 0; i + 1 < stroke.length; i += 2) {
    const p = v(stroke[i]!, stroke[i + 1]!);
    if (raw.length && dist(p, raw[raw.length - 1]!) < 0.5) continue;
    raw.push(p);
    rawT.push(opts.times?.[i / 2] ?? NaN);
  }
  if (raw.length < 2) return null;
  const L = pieceLen(raw);
  if (L < 2 * tol || raw.length < 4) return { pts: [raw[0]!, raw[raw.length - 1]!], round: [0, 0] };
  const M = Math.max(16, Math.min(240, Math.round(L / Math.max(0.5, tol / 2))));
  const { P: Q, T } = resample(raw, rawT, M, false);
  const w = 4;
  const turn = Q.map((b, i) => {
    if (i < w || i > M - 1 - w) return 0;
    const d1 = sub(b, Q[i - w]!),
      d2 = sub(Q[i + w]!, b);
    return Math.abs(Math.atan2(cross(d1, d2), dot(d1, d2)));
  });
  // how much slower than its usual pace the finger went through each sample
  const dt = Q.map((_, i) => (i >= 2 && i < M - 2 && T[i + 2]! > T[i - 2]! ? T[i + 2]! - T[i - 2]! : NaN));
  const known = dt.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  const median = known.length >= M / 2 ? known[Math.floor(known.length / 2)]! : NaN;
  const slow = dt.map((x) => (Number.isFinite(x) && median > 0 ? Math.max(0, Math.min(1, 1 - median / x)) : 0));
  const cand: { i: number; score: number }[] = [];
  for (let i = w; i <= M - 1 - w; i++) {
    const t = turn[i]!;
    let peak = true;
    for (let d = 1; d <= 3 && peak; d++) if ((turn[i + d] ?? 0) > t || (turn[i - d] ?? 0) > t) peak = false;
    if (peak && (t >= 55 * DEG || (t >= 35 * DEG && slow[i]! >= 0.5))) cand.push({ i, score: t });
  }
  cand.sort((a, b) => b.score - a.score);
  const corners: number[] = [];
  for (const c of cand) if (!corners.some((o) => Math.abs(o - c.i) < 5)) corners.push(c.i);
  const cuts = [0, ...corners.sort((a, b) => a - b), M - 1];
  const pieces = cuts.slice(0, -1).map((a, k) => Q.slice(a, cuts[k + 1]! + 1));
  const straight = pieces.map((pts) => {
    const a = pts[0]!,
      b = pts[pts.length - 1]!;
    const chord = dist(a, b);
    if (chord < 1e-6) return false;
    let dev = 0;
    for (const p of pts) dev = Math.max(dev, Math.abs(cross(sub(b, a), sub(p, a))) / chord);
    return dev <= Math.max(0.075 * chord, Math.min(0.35 * tol, 0.2 * chord));
  });
  const lineOf = pieces.map((pts, k) => (straight[k] ? fitLine(pts.slice(Math.floor(pts.length * 0.15), Math.ceil(pts.length * 0.85) + 1)) : null));
  const at = cuts.map((c, k) => {
    const q = Q[c]!;
    if (k === 0 || k === cuts.length - 1) return q;
    const before = lineOf[k - 1],
      after = lineOf[k];
    if (before && after) {
      const x = intersect(before, after);
      const room = 0.35 * Math.min(pieceLen(pieces[k - 1]!), pieceLen(pieces[k]!));
      if (x && dist(x, q) <= room) return x;
    }
    return q;
  });
  const pts: Vec[] = [at[0]!];
  const round: number[] = [0];
  pieces.forEach((piece, k) => {
    const A = at[k]!,
      B = at[k + 1]!;
    if (!straight[k]) {
      for (const p of roundPoints(piece, A, B, Math.max(0.5 * tol, 0.025 * pieceLen(piece)), 8)) {
        pts.push(p);
        round.push(1);
      }
    }
    pts.push(B);
    round.push(0);
  });
  return { pts, round };
}

/**
 * A border outline (a fitted Bézier path) as a form: its sharp anchors are the corners, and each
 * run between them is straight or flows through as few round points as follow it within `tol`.
 */
export function outlineForm(path: CurvePath, tol: number, maxPoints = 120): PolyForm | null {
  // a ragged outline (a flood of a photographed plan, a join of many marks) is followed more
  // loosely until it has a handful of handles a finger can use; failing that it stays an outline
  for (let t = tol, k = 0; k < 5; k++, t *= 2) {
    const f = outlineFormAt(path, t);
    if (!f) return null;
    if (f.pts.length / 2 <= maxPoints) return f;
  }
  return null;
}

function outlineFormAt(path: CurvePath, tol: number): PolyForm | null {
  const n = path.pts.length / 2;
  if (n < 2) return null;
  const sharp: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = v(path.pts[2 * i]!, path.pts[2 * i + 1]!);
    const hin = sub(a, v(path.h[4 * i]!, path.h[4 * i + 1]!));
    const hout = sub(v(path.h[4 * i + 2]!, path.h[4 * i + 3]!), a);
    const li = Math.hypot(hin.x, hin.y),
      lo = Math.hypot(hout.x, hout.y);
    // a smooth anchor's handles lie on one line; anything else is a corner
    if (li < 1e-6 || lo < 1e-6 || Math.abs(cross(hin, hout)) / (li * lo) > Math.sin(8 * DEG) || dot(hin, hout) < 0) sharp.push(i);
  }
  const at: number[] = [];
  const flat = flattenPath(path.pts, path.h, Math.max(1, tol / 4), at);
  const P: Vec[] = [];
  for (let i = 0; i + 1 < flat.length; i += 2) P.push(v(flat[i]!, flat[i + 1]!));
  if (P.length < 3) return null;
  const corners = sharp.map((k) => at[k]!).filter((i) => i !== undefined && i < P.length);
  const uniq = [...new Set(corners)].sort((a, b) => a - b);
  const exact = { straight: tol, curve: tol };
  const res = uniq.length ? fitPieces(P, uniq, new Map(uniq.map((i) => [i, P[i]!])), tol, { maxRound: 12, refine: false, exact }) : roundLoop(P, tol, 16, tol);
  return dropFlatCorners(res.form, 1.5 * tol);
}

/**
 * Corners that hardly turn — a border's jogs of a pixel where it follows the ink — are no handles
 * anyone wants: a corner between two straight sides that lies within `tol` of the line joining its
 * neighbours goes, the flattest first.
 */
function dropFlatCorners(f: PolyForm, tol: number): PolyForm {
  const P = [];
  for (let i = 0; i + 1 < f.pts.length; i += 2) P.push(v(f.pts[i]!, f.pts[i + 1]!));
  const round = f.round.slice();
  for (;;) {
    const n = P.length;
    if (n <= 3) break;
    let best = -1,
      bd = tol;
    for (let i = 0; i < n; i++) {
      const a = (i - 1 + n) % n,
        b = (i + 1) % n;
      if (round[i] || round[a] || round[b]) continue;
      const d = segDist(P[i]!, P[a]!, P[b]!);
      if (d <= bd) {
        bd = d;
        best = i;
      }
    }
    if (best < 0) break;
    P.splice(best, 1);
    round.splice(best, 1);
  }
  return { f: 'poly', pts: P.flatMap((p) => [p.x, p.y]), round };
}

/* ------------------------------------------------------------------ pieces */

interface Fitted {
  form: PolyForm;
  /** The straight sides, as fitted lines (for the rectangle's turn). */
  lines: { a: Vec; b: Vec; len: number }[];
}

/**
 * The loop between its corners: each piece straight when it keeps close to its chord, else the
 * fewest evenly spaced round points whose curve follows it. Corners between two straight pieces
 * are where those two lines cross.
 */
function fitPieces(Q: Vec[], corners: number[], exact: Map<number, Vec>, tol: number, opts: { maxRound?: number; refine?: boolean; exact?: { straight: number; curve: number } } = {}): Fitted {
  const { maxRound = 6, refine = true } = opts;
  const n = Q.length;
  const cs = [...corners].sort((a, b) => a - b);
  const pieces = cs.map((c, k) => {
    const d = cs[(k + 1) % cs.length]!;
    const pts: Vec[] = [];
    for (let i = c; ; i = (i + 1) % n) {
      pts.push(Q[i]!);
      if (i === d && pts.length > 1) break;
      if (pts.length > n + 1) break;
    }
    return pts;
  });
  const straight = pieces.map((pts) => {
    const a = pts[0]!,
      b = pts[pts.length - 1]!;
    const chord = dist(a, b);
    if (cs.length === 1 || chord < 1e-6) return false;
    let dev = 0;
    for (const p of pts) dev = Math.max(dev, Math.abs(cross(sub(b, a), sub(p, a))) / chord);
    // a finger's straight side bows a little; an outline's is exact
    return dev <= (opts.exact ? opts.exact.straight : Math.max(0.075 * chord, Math.min(0.35 * tol, 0.2 * chord)));
  });
  // the straight pieces' own lines, fitted to their middles (away from the finger's rounding at the corners)
  const lineOf = pieces.map((pts, k) => (straight[k] ? fitLine(pts.slice(Math.floor(pts.length * 0.15), Math.ceil(pts.length * 0.85) + 1)) : null));
  const cornerAt = cs.map((c, k) => {
    const pin = exact.get(c);
    if (pin) return pin;
    const before = lineOf[(k - 1 + cs.length) % cs.length],
      after = lineOf[k];
    const q = Q[c]!;
    if (refine && before && after && cs.length > 1) {
      const x = intersect(before, after);
      const room = 0.35 * Math.min(pieceLen(pieces[(k - 1 + cs.length) % cs.length]!), pieceLen(pieces[k]!));
      if (x && dist(x, q) <= room) return x;
    }
    const line = before ?? after;
    return refine && line && cs.length > 1 ? project(line, q) : q;
  });
  const pts: number[] = [];
  const round: number[] = [];
  const lines: Fitted['lines'] = [];
  pieces.forEach((piece, k) => {
    const A = cornerAt[k]!,
      B = cornerAt[(k + 1) % cs.length]!;
    pts.push(A.x, A.y);
    round.push(0);
    if (straight[k]) {
      lines.push({ a: A, b: B, len: dist(A, B) });
      return;
    }
    const inner = roundPoints(piece, A, B, opts.exact ? opts.exact.curve : Math.max(0.5 * tol, 0.025 * pieceLen(piece)), maxRound);
    for (const p of inner) {
      pts.push(p.x, p.y);
      round.push(1);
    }
  });
  return { form: { f: 'poly', pts, round }, lines };
}

/** The fewest evenly spaced round points between corners A and B whose curve follows the piece. */
function roundPoints(piece: Vec[], A: Vec, B: Vec, allow: number, maxRound: number): Vec[] {
  let best: Vec[] = [];
  let bestDev = Infinity;
  for (let m = 1; m <= maxRound; m++) {
    const inner = spaced(piece, m);
    const chain = [A, ...inner, B];
    const dev = deviation(piece, chainPolyline(chain, false));
    if (dev < bestDev) {
      bestDev = dev;
      best = inner;
    }
    if (dev <= allow) break;
  }
  return best;
}

/** A loop with no corners: the fewest evenly spaced round points that follow it. */
function roundLoop(Q: Vec[], tol: number, maxK = 12, exact?: number): Fitted {
  const allow = exact ?? Math.max(0.5 * tol, 0.02 * perimeter(Q));
  let best: Vec[] = [];
  let bestDev = Infinity;
  for (let k = 3; k <= maxK; k++) {
    const pts = Array.from({ length: k }, (_, i) => Q[Math.round((i * Q.length) / k) % Q.length]!);
    const dev = deviation(Q, chainPolyline(pts, true));
    if (dev < bestDev) {
      bestDev = dev;
      best = pts;
    }
    if (dev <= allow) break;
  }
  return { form: { f: 'poly', pts: best.flatMap((p) => [p.x, p.y]), round: best.map(() => 1) }, lines: [] };
}

/** The outline of a run of points (all round but the ends of an open run) as a polyline. */
function chainPolyline(P: Vec[], closed: boolean): Vec[] {
  const n = P.length;
  const tan = P.map((b, i) => {
    if (!closed && (i === 0 || i === n - 1)) return null;
    const a = P[(i - 1 + n) % n]!,
      c = P[(i + 1) % n]!;
    return circleTangent(a, b, c);
  });
  const out: Vec[] = [P[0]!];
  const last = closed ? n : n - 1;
  for (let i = 0; i < last; i++) {
    const a = P[i]!;
    let p0 = a;
    for (const c of edgeCubics(a, P[(i + 1) % n]!, tan[i]!, tan[(i + 1) % n]!)) {
      for (let s = 1; s <= 8; s++) out.push(bez(p0, c, s / 8));
      p0 = c.to;
    }
  }
  return out;
}

function circleTangent(a: Vec, b: Vec, c: Vec): Vec {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  const turn = cross(sub(b, a), sub(c, b));
  const span = dist(a, c);
  if (Math.abs(d) < 1e-9 * span * span || Math.abs(turn) < 1e-9 * span * span) return norm(sub(c, a));
  const a2 = a.x * a.x + a.y * a.y,
    b2 = b.x * b.x + b.y * b.y,
    c2 = c.x * c.x + c.y * c.y;
  const o = v((a2 * (b.y - c.y) + b2 * (c.y - a.y) + c2 * (a.y - b.y)) / d, (a2 * (c.x - b.x) + b2 * (a.x - c.x) + c2 * (b.x - a.x)) / d);
  const r = sub(b, o);
  return norm(v(-r.y * Math.sign(turn), r.x * Math.sign(turn)));
}

function bez(p0: Vec, c: Cubic, t: number): Vec {
  const s = 1 - t;
  return v(s * s * s * p0.x + 3 * s * s * t * c.c1.x + 3 * s * t * t * c.c2.x + t * t * t * c.to.x, s * s * s * p0.y + 3 * s * s * t * c.c1.y + 3 * s * t * t * c.c2.y + t * t * t * c.to.y);
}

/* ----------------------------------------------------------------- corners */

/** The outline's turn at sample i, measured across w samples either side (radians, 0 … π). */
function turnAt(Q: Vec[], i: number, w: number): number {
  const n = Q.length;
  const a = Q[(i - w + n) % n]!,
    b = Q[i]!,
    c = Q[(i + w) % n]!;
  const d1 = sub(b, a),
    d2 = sub(c, b);
  return Math.abs(Math.atan2(cross(d1, d2), dot(d1, d2)));
}

/** How much slower than usual the finger went through each sample (0 = usual pace or unknown, 1 = stopped). */
function slowness(T: number[] | null): number[] {
  const n = N;
  if (!T || T.some((t) => !Number.isFinite(t))) return new Array<number>(n).fill(0);
  const dt: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = T[(i - 2 + n) % n]!,
      b = T[(i + 2) % n]!;
    // across the seam (the loop's end back to its start) nothing is known
    dt.push(b > a && b - a < 5000 ? b - a : NaN);
  }
  const known = dt.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  if (known.length < n / 2) return new Array<number>(n).fill(0);
  const median = known[Math.floor(known.length / 2)]!;
  return dt.map((x) => (Number.isFinite(x) && median > 0 ? Math.max(0, Math.min(1, 1 - median / x)) : 0));
}

function findCorners(turn: number[], slow: number[], pinned: number[]): number[] {
  const n = turn.length;
  const cand: { i: number; score: number }[] = [];
  for (let i = 0; i < n; i++) {
    const t = turn[i]!;
    let peak = true;
    for (let d = 1; d <= 3 && peak; d++) if (turn[(i + d) % n]! > t || turn[(i - d + n) % n]! > t) peak = false;
    if (!peak) continue;
    const isPinned = pinned.some((j) => cyclicGap(i, j, n) <= 3);
    // a sharp turn; a clear turn the finger slowed down for; any real turn where it rested
    if (t >= 55 * DEG || (t >= 35 * DEG && slow[i]! >= 0.5) || (isPinned && t >= 14 * DEG)) cand.push({ i, score: t + (isPinned ? Math.PI : 0) });
  }
  // a rest counts even where the turn peaks a sample or two away
  for (const j of pinned) if (!cand.some((c) => cyclicGap(c.i, j, n) <= 3) && turn[j]! >= 14 * DEG) cand.push({ i: j, score: turn[j]! + Math.PI });
  cand.sort((a, b) => b.score - a.score);
  const out: number[] = [];
  for (const c of cand) if (!out.some((o) => cyclicGap(o, c.i, n) < 5)) out.push(c.i);
  return out.sort((a, b) => a - b);
}

function cyclicGap(a: number, b: number, n: number): number {
  const d = Math.abs(a - b) % n;
  return Math.min(d, n - d);
}

function nearestPinned(pinned: readonly Vec[], q: Vec): Vec {
  let best = pinned[0]!;
  for (const p of pinned) if (dist(p, q) < dist(best, q)) best = p;
  return best;
}

function rectLike(f: PolyForm, lines: Fitted['lines']): boolean {
  if (f.pts.length !== 8 || f.round.some((r) => r) || lines.length !== 4) return false;
  for (let i = 0; i < 4; i++) {
    const a = v(f.pts[2 * i]!, f.pts[2 * i + 1]!);
    const b = v(f.pts[(2 * i + 2) % 8]!, f.pts[(2 * i + 3) % 8]!);
    const c = v(f.pts[(2 * i + 4) % 8]!, f.pts[(2 * i + 5) % 8]!);
    const u = sub(b, a),
      w = sub(c, b);
    const ang = Math.abs(Math.atan2(cross(u, w), dot(u, w)));
    if (Math.abs(ang - Math.PI / 2) > 22 * DEG) return false;
  }
  return true;
}

/* -------------------------------------------------------------- rectangles */

/**
 * The rectangle a loop stands for: turned the way its straight sides run (their directions
 * folded onto one quarter turn and averaged by length), else its spread; each side where the
 * middle of the loop's points nearest to that side lie.
 */
function rectOf(Q: Vec[], mom: Moments, lines: Fitted['lines']): Form {
  let angle: number;
  if (lines.length >= 2) {
    let sx = 0,
      sy = 0;
    for (const l of lines) {
      const a = Math.atan2(l.b.y - l.a.y, l.b.x - l.a.x);
      sx += Math.cos(4 * a) * l.len;
      sy += Math.sin(4 * a) * l.len;
    }
    angle = Math.atan2(sy, sx) / 4;
  } else angle = tightestTurn(Q);
  // within a few degrees of square to the screen, it is square to the screen (a plan turned a
  // little still has its rectangle's corners landed on its own corners)
  const quarter = Math.PI / 2;
  const off = angle - Math.round(angle / quarter) * quarter;
  if (Math.abs(off) < 4 * DEG) angle -= off;
  const c = v(mom.cx, mom.cy);
  const R = Q.map((q) => rotate(sub(q, c), -angle));
  let x0 = Infinity,
    x1 = -Infinity,
    y0 = Infinity,
    y1 = -Infinity;
  for (const r of R) {
    x0 = Math.min(x0, r.x);
    x1 = Math.max(x1, r.x);
    y0 = Math.min(y0, r.y);
    y1 = Math.max(y1, r.y);
  }
  const sides: number[][] = [[], [], [], []]; // left, right, top, bottom
  for (const r of R) {
    const d = [r.x - x0, x1 - r.x, r.y - y0, y1 - r.y];
    const k = d.indexOf(Math.min(...d));
    sides[k]!.push(k < 2 ? r.x : r.y);
  }
  const med = (a: number[], fallback: number) => (a.length >= 3 ? a.sort((p, q) => p - q)[Math.floor(a.length / 2)]! : fallback);
  const l = med(sides[0]!, x0),
    r = med(sides[1]!, x1),
    t = med(sides[2]!, y0),
    b = med(sides[3]!, y1);
  let corners = [v(l, t), v(r, t), v(r, b), v(l, b)].map((p) => {
    const q = rotate(p, angle);
    return v(q.x + c.x, q.y + c.y);
  });
  // wound the way the loop was drawn
  if (Math.sign(polyArea(corners)) !== Math.sign(mom.area)) corners = corners.reverse();
  return { f: 'poly', pts: corners.flatMap((p) => [p.x, p.y]), round: [0, 0, 0, 0] };
}

/** The turn (radians, within a quarter turn) of the smallest rectangle round the points. */
function tightestTurn(Q: Vec[]): number {
  const area = (a: number) => {
    const c = Math.cos(a),
      s = Math.sin(a);
    let x0 = Infinity,
      x1 = -Infinity,
      y0 = Infinity,
      y1 = -Infinity;
    for (const q of Q) {
      const x = q.x * c + q.y * s,
        y = -q.x * s + q.y * c;
      x0 = Math.min(x0, x);
      x1 = Math.max(x1, x);
      y0 = Math.min(y0, y);
      y1 = Math.max(y1, y);
    }
    return (x1 - x0) * (y1 - y0);
  };
  let best = 0,
    ba = Infinity;
  for (let d = 0; d < 90; d += 1) {
    const a = area(d * DEG);
    if (a < ba) {
      ba = a;
      best = d * DEG;
    }
  }
  for (let d = -1; d <= 1; d += 0.1) {
    const a = area(best + d * DEG);
    if (a < ba) {
      ba = a;
      best += d * DEG;
    }
  }
  return best;
}

function formCorners(f: Form): Vec[] {
  if (f.f !== 'poly') return [];
  const out: Vec[] = [];
  for (let i = 0; i + 1 < f.pts.length; i += 2) out.push(v(f.pts[i]!, f.pts[i + 1]!));
  return out;
}

/** Distance from q to a closed polygon's outline. */
function distToLoop(q: Vec, P: Vec[]): number {
  let best = Infinity;
  for (let i = 0; i < P.length; i++) best = Math.min(best, segDist(q, P[i]!, P[(i + 1) % P.length]!));
  return best;
}

/* ---------------------------------------------------------------- ellipses */

interface Moments {
  area: number; // signed
  cx: number;
  cy: number;
  cxx: number;
  cyy: number;
  cxy: number;
}

/** The area, centroid and spread of the region a loop encloses (Green's theorem). */
function moments(Q: Vec[]): Moments {
  let a = 0,
    sx = 0,
    sy = 0,
    sxx = 0,
    syy = 0,
    sxy = 0;
  const n = Q.length;
  // about the first point, for precision far from the origin
  const o = Q[0]!;
  for (let i = 0; i < n; i++) {
    const p = sub(Q[i]!, o),
      q = sub(Q[(i + 1) % n]!, o);
    const c = p.x * q.y - q.x * p.y;
    a += c;
    sx += (p.x + q.x) * c;
    sy += (p.y + q.y) * c;
    sxx += (p.x * p.x + p.x * q.x + q.x * q.x) * c;
    syy += (p.y * p.y + p.y * q.y + q.y * q.y) * c;
    sxy += (p.x * q.y + 2 * p.x * p.y + 2 * q.x * q.y + q.x * p.y) * c;
  }
  a /= 2;
  if (Math.abs(a) < 1e-9) return { area: 0, cx: o.x, cy: o.y, cxx: 0, cyy: 0, cxy: 0 };
  const cx = sx / (6 * a),
    cy = sy / (6 * a);
  return { area: a, cx: cx + o.x, cy: cy + o.y, cxx: sxx / (12 * a) - cx * cx, cyy: syy / (12 * a) - cy * cy, cxy: sxy / (24 * a) - cx * cy };
}

/** The ellipse with the same area moments (a filled ellipse's spread along an axis is a²/4). */
function ellipseOf(m: Moments): { cx: number; cy: number; rx: number; ry: number; rot: number } {
  const tr = (m.cxx + m.cyy) / 2;
  const det = Math.sqrt(Math.max(0, ((m.cxx - m.cyy) / 2) ** 2 + m.cxy * m.cxy));
  const l1 = tr + det,
    l2 = Math.max(1e-9, tr - det);
  const rot = 0.5 * Math.atan2(2 * m.cxy, m.cxx - m.cyy);
  return { cx: m.cx, cy: m.cy, rx: 2 * Math.sqrt(l1), ry: 2 * Math.sqrt(l2), rot };
}

/* ---------------------------------------------------------------- geometry */

function resample(P: Vec[], T: number[], n: number, closed: boolean): { P: Vec[]; T: number[] } {
  const segs = closed ? P.length : P.length - 1;
  const cum = [0];
  for (let i = 0; i < segs; i++) cum.push(cum[i]! + dist(P[i]!, P[(i + 1) % P.length]!));
  const L = cum[segs]!;
  const outP: Vec[] = [],
    outT: number[] = [];
  const count = closed ? n : n;
  let k = 0;
  for (let j = 0; j < count; j++) {
    const s = closed ? (j * L) / n : (j * L) / Math.max(1, n - 1);
    while (k < segs - 1 && cum[k + 1]! < s) k++;
    const seg = cum[k + 1]! - cum[k]!;
    const t = seg > 0 ? (s - cum[k]!) / seg : 0;
    const a = P[k]!,
      b = P[(k + 1) % P.length]!;
    outP.push(v(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t));
    const ta = T[k]!,
      tb = T[(k + 1) % T.length]!;
    // across the seam of a closed loop the time jumps: keep the nearer end's
    outT.push(closed && k === segs - 1 ? (t < 0.5 ? ta : tb) : ta + (tb - ta) * t);
  }
  return { P: outP, T: outT };
}

/**
 * The loop a closing stroke makes: where the finger ran on past its start, the stroke is cut
 * where it crosses itself (the overshoot and the lead-in are dropped); else the gap closes.
 */
function closeLoop(P: Vec[], T: number[]): { P: Vec[]; T: number[] } {
  const n = P.length;
  const lo = Math.floor(n * 0.4),
    hi = Math.ceil(n * 0.6);
  for (let i = n - 2; i >= hi; i--) {
    for (let j = 0; j < lo && j + 1 < i; j++) {
      const x = segX(P[i]!, P[i + 1]!, P[j]!, P[j + 1]!);
      if (!x) continue;
      return { P: [x, ...P.slice(j + 1, i + 1)], T: [T[j + 1]!, ...T.slice(j + 1, i + 1)] };
    }
  }
  // no crossing: the finger may have run on alongside its start without crossing it. The loop is
  // then from the start to where the end comes nearest it again (and likewise a lead-in: from where
  // the start comes nearest the end) — never both ends doubled back over each other
  let x0 = Infinity,
    y0 = -Infinity,
    x1 = Infinity,
    y1 = -Infinity;
  for (const p of P) {
    x0 = Math.min(x0, p.x);
    y0 = Math.max(y0, p.x);
    x1 = Math.min(x1, p.y);
    y1 = Math.max(y1, p.y);
  }
  const reach = 0.12 * Math.hypot(y0 - x0, y1 - x1);
  let best = { i: n - 1, j: 0, d: dist(P[n - 1]!, P[0]!) };
  for (let i = hi; i < n; i++) {
    for (let j = 0; j < lo; j++) {
      const d = dist(P[i]!, P[j]!);
      // the nearest meeting; ties go to the one keeping more of the stroke
      if (d < best.d - 0.5 || (d <= best.d + 0.5 && i - j > best.i - best.j)) best = { i, j, d };
    }
  }
  if (best.d > reach || (best.i === n - 1 && best.j === 0)) return { P, T };
  return { P: P.slice(best.j, best.i + 1), T: T.slice(best.j, best.i + 1) };
}

function segX(a: Vec, b: Vec, c: Vec, d: Vec): Vec | null {
  const r = sub(b, a),
    s = sub(d, c);
  const den = cross(r, s);
  if (Math.abs(den) < 1e-12) return null;
  const t = cross(sub(c, a), s) / den,
    u = cross(sub(c, a), r) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? v(a.x + r.x * t, a.y + r.y * t) : null;
}

function perimeter(Q: Vec[]): number {
  let s = 0;
  for (let i = 0; i < Q.length; i++) s += dist(Q[i]!, Q[(i + 1) % Q.length]!);
  return s;
}

function pieceLen(P: Vec[]): number {
  let s = 0;
  for (let i = 0; i + 1 < P.length; i++) s += dist(P[i]!, P[i + 1]!);
  return s;
}

function polyArea(P: Vec[]): number {
  let a = 0;
  for (let i = 0; i < P.length; i++) a += cross(P[i]!, P[(i + 1) % P.length]!);
  return a / 2;
}

/** m points evenly spaced by length along an open run, excluding its ends. */
function spaced(P: Vec[], m: number): Vec[] {
  const L = pieceLen(P);
  const out: Vec[] = [];
  let k = 0,
    acc = 0;
  for (let j = 1; j <= m; j++) {
    const s = (j * L) / (m + 1);
    while (k + 1 < P.length - 1 && acc + dist(P[k]!, P[k + 1]!) < s) {
      acc += dist(P[k]!, P[k + 1]!);
      k++;
    }
    const seg = dist(P[k]!, P[k + 1]!) || 1;
    const t = Math.min(1, Math.max(0, (s - acc) / seg));
    out.push(v(P[k]!.x + (P[k + 1]!.x - P[k]!.x) * t, P[k]!.y + (P[k + 1]!.y - P[k]!.y) * t));
  }
  return out;
}

/** The farthest any of `pts` lies from the polyline. */
function deviation(pts: Vec[], line: Vec[]): number {
  let worst = 0;
  for (const p of pts) {
    let best = Infinity;
    for (let i = 0; i + 1 < line.length; i++) best = Math.min(best, segDist(p, line[i]!, line[i + 1]!));
    worst = Math.max(worst, best);
  }
  return worst;
}

function segDist(p: Vec, a: Vec, b: Vec): number {
  const ab = sub(b, a);
  const l2 = dot(ab, ab) || 1;
  const t = Math.max(0, Math.min(1, dot(sub(p, a), ab) / l2));
  return Math.hypot(p.x - (a.x + ab.x * t), p.y - (a.y + ab.y * t));
}

interface Line {
  p: Vec; // a point on it
  d: Vec; // unit direction
}

/** The total-least-squares line through points. */
function fitLine(P: Vec[]): Line | null {
  if (P.length < 2) return null;
  let mx = 0,
    my = 0;
  for (const p of P) {
    mx += p.x;
    my += p.y;
  }
  mx /= P.length;
  my /= P.length;
  let sxx = 0,
    syy = 0,
    sxy = 0;
  for (const p of P) {
    sxx += (p.x - mx) ** 2;
    syy += (p.y - my) ** 2;
    sxy += (p.x - mx) * (p.y - my);
  }
  const a = 0.5 * Math.atan2(2 * sxy, sxx - syy);
  return { p: v(mx, my), d: v(Math.cos(a), Math.sin(a)) };
}

function intersect(a: Line, b: Line): Vec | null {
  const den = cross(a.d, b.d);
  if (Math.abs(den) < Math.sin(12 * DEG)) return null; // nearly parallel: no clear corner
  const t = cross(sub(b.p, a.p), b.d) / den;
  return v(a.p.x + a.d.x * t, a.p.y + a.d.y * t);
}

function project(l: Line, q: Vec): Vec {
  const t = dot(sub(q, l.p), l.d);
  return v(l.p.x + l.d.x * t, l.p.y + l.d.y * t);
}

function rotate(a: Vec, t: number): Vec {
  const c = Math.cos(t),
    s = Math.sin(t);
  return v(a.x * c - a.y * s, a.x * s + a.y * c);
}

function norm(a: Vec): Vec {
  const l = Math.hypot(a.x, a.y) || 1;
  return v(a.x / l, a.y / l);
}
